import React, { useEffect, useState } from "react";
import { Smartphone, Share, Plus } from "lucide-react";

/**
 * Small homepage card that nudges visitors to install the PWA.
 * Hidden when already standalone, when the bottom banner was dismissed,
 * or when there is no install signal (no beforeinstallprompt AND not iOS).
 * On iOS it shows the manual "Share → Add to Home Screen" instructions.
 * On Android/Desktop Chrome it triggers the native install prompt.
 */
const DISMISS_KEY = "sm_pwa_dismissed_at";
const DISMISS_TTL_DAYS = 14;

function isStandalone() {
    if (typeof window === "undefined") return false;
    return (
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true
    );
}

function isIOS() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function wasDismissedRecently() {
    try {
        const v = localStorage.getItem(DISMISS_KEY);
        if (!v) return false;
        return (Date.now() - Number(v)) / (1000 * 60 * 60 * 24) < DISMISS_TTL_DAYS;
    } catch {
        return false;
    }
}

export default function InstallAppCard() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [show, setShow] = useState(false);
    const [iosMode, setIosMode] = useState(false);

    useEffect(() => {
        if (isStandalone() || wasDismissedRecently()) return;

        const onPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            setShow(true);
        };
        window.addEventListener("beforeinstallprompt", onPrompt);

        if (isIOS()) {
            setIosMode(true);
            setShow(true);
        }

        const onInstalled = () => {
            setShow(false);
            setDeferredPrompt(null);
        };
        window.addEventListener("appinstalled", onInstalled);

        return () => {
            window.removeEventListener("beforeinstallprompt", onPrompt);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const handleInstall = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        try {
            await deferredPrompt.userChoice;
        } catch {
            // ignore
        }
        setDeferredPrompt(null);
        setShow(false);
    };

    if (!show) return null;

    return (
        <section
            data-testid="install-app-card"
            className="border-t border-zinc-900 bg-[#09090b]"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
                <div className="card-tu flex flex-col md:flex-row md:items-center gap-6 border-orange-500/20">
                    <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-orange-600 to-red-600 flex items-center justify-center">
                        <Smartphone size={26} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-1">
                            Sponsormania sur votre écran d&apos;accueil
                        </div>
                        <h3 className="font-display text-2xl md:text-3xl uppercase tracking-tight text-white">
                            Installez l&apos;app — accès en un tap
                        </h3>
                        {iosMode ? (
                            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                                Sur iPhone : appuyez sur
                                <Share size={14} className="inline mx-1 -mt-1 text-orange-500" />
                                puis « Sur l&apos;écran d&apos;accueil »
                                <Plus size={14} className="inline mx-1 -mt-1 text-orange-500" />.
                            </p>
                        ) : (
                            <p className="mt-2 text-sm text-zinc-400">
                                Lancement instantané, notifications de vos clubs préférés, et utilisable hors-ligne.
                            </p>
                        )}
                    </div>
                    {!iosMode && (
                        <button
                            type="button"
                            onClick={handleInstall}
                            data-testid="install-app-card-cta"
                            className="self-stretch md:self-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white font-bold uppercase tracking-widest text-xs rounded-lg transition-all active:scale-[0.98]"
                        >
                            <Smartphone size={16} /> Installer l&apos;app
                        </button>
                    )}
                </div>
            </div>
        </section>
    );
}
